/** @file Checks a puppet's node tree: known parents, no cycles, one root, mirror twins, IK depth. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { PuppetNode } from '@zilo/pose3d-format/types/puppet';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';

type Nodes = Record<string, PuppetNode>;

function checkParents(nodes: Nodes): void {
  for (const [name, node] of Object.entries(nodes)) {
    if (node.parent !== undefined && !Object.hasOwn(nodes, node.parent)) {
      throwError(ERROR_CODES.NODE_PARENT_UNKNOWN, { node: name, parent: node.parent }, { path: `/nodes/${name}/parent` });
    }
  }
}

/** Ancestor count of `name`, or -1 when walking up returns to `name` (bounded by the node count). */
function depthOf(nodes: Nodes, name: string): number {
  const limit = Object.keys(nodes).length;
  let cur = nodes[name]?.parent;
  for (let depth = 1; cur !== undefined; depth++) {
    if (cur === name) return -1;
    if (depth > limit) return limit;
    cur = nodes[cur]?.parent;
    if (cur === undefined) return depth;
  }
  return 0;
}

function checkTree(nodes: Nodes): Map<string, number> {
  const depths = new Map<string, number>();
  for (const name of Object.keys(nodes)) {
    const depth = depthOf(nodes, name);
    if (depth < 0) throwError(ERROR_CODES.NODE_CYCLE, { node: name }, { path: `/nodes/${name}/parent`, hint: 'nodes form a tree' });
    depths.set(name, depth);
  }
  const roots = Object.keys(nodes).filter((n) => nodes[n]?.parent === undefined);
  if (roots.length !== 1) {
    throwError(ERROR_CODES.NODE_ROOT_COUNT, { roots }, { path: '/nodes', hint: 'exactly one node has no parent' });
  }
  return depths;
}

function checkMirrors(nodes: Nodes): void {
  const namedBy = new Map<string, string>();
  for (const [name, node] of Object.entries(nodes)) {
    const twin = node.mirror;
    if (twin === undefined) continue;
    const path = `/nodes/${name}/mirror`;
    if (twin === name) failField(path, 'another node', twin);
    if (!Object.hasOwn(nodes, twin)) throwError(ERROR_CODES.NODE_MIRROR_UNKNOWN, { node: name, mirror: twin }, { path });
    const back = nodes[twin]?.mirror;
    if (namedBy.has(twin) || (back !== undefined && back !== name)) {
      throwError(ERROR_CODES.NODE_DUPLICATE_MIRROR, { node: name, mirror: twin, other: namedBy.get(twin) ?? back }, { path });
    }
    namedBy.set(twin, name);
  }
}

/** Throws the first tree error of `nodes` (paths under `/nodes`); never loops on a cycle. */
export function validateNodes(nodes: Nodes): void {
  checkParents(nodes);
  const depths = checkTree(nodes);
  checkMirrors(nodes);
  for (const [name, node] of Object.entries(nodes)) {
    const depth = depths.get(name) ?? 0;
    if (node.ik !== undefined && node.ik > depth) failField(`/nodes/${name}/ik`, `chain length <= ${depth} (ancestors)`, node.ik);
  }
}
